import React, { useState } from 'react';
import { LogoLockup } from './Icons';
import { pagarSuscripcion } from './PagosEnLinea';
import { getToken } from '../utils/api';

// Pantalla que ve un estudio cuando no puede operar: vencido,
// vencido, suspendido, o en la ventana de gracia de solo lectura. Sin esto el
// panel mostraría errores sueltos justo cuando queremos que el cliente renueve.
const C = { navy: '#0D1B35', blue2: '#2756A8', line: '#DDE6F3', muted: '#6B82A0' };

export type DatosPago = {
  banco: string | null;
  tipoCuenta: string | null;
  numero: string | null;
  titular: string | null;
  rut: string | null;
  email: string;
  /** Micrositio de pago del banco. Va como enlace y no como iframe a
   *  propósito: el portal responde frame-ancestors 'self' y X-Frame-Options
   *  SAMEORIGIN, así que embebido acá el recuadro sale en blanco. */
  link?: string | null;
  /** Se puede pagar en línea con Banchile Pagos (Web Checkout): el estudio
   *  queda renovado al instante, sin esperar a que se registre el pago. */
  enLinea?: boolean;
  /** Sesión corta solo para pagar, cuando el bloqueo vino del login. */
  token?: string;
} | null;

const Fila: React.FC<{ k: string; v?: string | null }> = ({ k, v }) => (
  <div style={{ display: 'flex', gap: 10, fontSize: 14, padding: '5px 0', borderBottom: `1px solid ${C.line}` }}>
    <span style={{ color: C.muted, minWidth: 92 }}>{k}</span>
    <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{v || '—'}</strong>
  </div>
);

// Cómo pagar para renovar: el link del banco primero —se resuelve solo, sin
// esperar a que alguien acá revise una transferencia— y la cuenta debajo, para
// el estudio que prefiere transferir. Cualquiera de las dos puede faltar.
export const MediosDePago: React.FC<{
  pago: DatosPago;
  /** Qué pasa después de pagar. Cambia según para qué se paga —renovar el plan
   *  o cargarle saldo a Chatbularis— y es lo único que cambia: el resto, sobre
   *  todo decir quién cobra, vale igual en los dos casos y por eso no se
   *  duplica. */
  cierre?: React.ReactNode;
}> = ({ pago, cierre }) => {
  if (!pago) return (
    <p style={{ fontSize: 14, color: C.muted }}>
      Escríbenos a <a href="mailto:contacto@tabularis.cl" style={{ color: C.blue2 }}>contacto@tabularis.cl</a> y te enviamos los datos para pagar.
    </p>
  );
  return (
    <div style={{ textAlign: 'left' }}>
      {pago.link && (
        <>
          <a
            href={pago.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block', textAlign: 'center', padding: '13px 20px', borderRadius: 10, background: C.blue2, color: '#fff', textDecoration: 'none', fontSize: 15, fontWeight: 700 }}
          >
            Pagar en línea
          </a>
          {/* De dónde sale el pago. Se dice acá y en el correo: un botón de
              pagar sin decir quién cobra es la forma que tiene una estafa, y
              el estudio que desconfía —con razón— no paga. */}
          <p style={{ fontSize: 12.5, color: C.muted, textAlign: 'center', margin: '8px 0 16px', lineHeight: 1.55 }}>
            Es el portal de pagos de <strong>Banco de Chile</strong>: se abre en otra pestaña y pagas en el sitio del
            banco, con tarjeta de débito o crédito y con las condiciones y cuotas que tenga la tuya.
            Tabularis no ve ni guarda los datos de tu tarjeta.
          </p>
        </>
      )}
      {pago.banco && (
        <div style={{ background: '#F8FAFD', border: `1px solid ${C.line}`, borderRadius: 12, padding: '16px 18px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>
            {pago.link ? 'O transfiere' : 'Datos para transferencia'}
          </div>
          <Fila k="Banco" v={pago.banco} />
          <Fila k="Cuenta" v={`${pago.tipoCuenta || ''} ${pago.numero || ''}`.trim()} />
          <Fila k="Titular" v={pago.titular} />
          <Fila k="RUT" v={pago.rut} />
          <Fila k="Correo" v={pago.email} />
        </div>
      )}
      <p style={{ fontSize: 13, color: C.muted, margin: '12px 0 0' }}>
        {cierre ?? <>Envía el comprobante a <a href={`mailto:${pago.email}`} style={{ color: C.blue2 }}>{pago.email}</a> y reactivamos tu estudio el mismo día.</>}
      </p>
    </div>
  );
};

type Props = {
  motivo: string; mensaje: string; pago?: DatosPago; lecturaHasta?: string | null; onSalir: () => void;
  /** Lo que va arriba de la tarjeta: el resumen de un pago recién hecho. */
  arriba?: React.ReactNode;
};

/** Pagar la renovación en línea desde el bloqueo: es la forma de salir de
 *  ahí sin esperar a nadie. Las rutas de pago siguen abiertas con el estudio
 *  vencido (ver plugins/auth.ts en el servidor). */
const PagarEnLinea: React.FC<{ token?: string }> = ({ token }) => {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const pagar = async () => {
    setOcupado(true); setError('');
    try { window.location.href = (await pagarSuscripcion(token)).url; }
    catch (e: any) { setError(e?.message || 'No pudimos abrir el pago. Inténtalo de nuevo.'); setOcupado(false); }
  };
  return (
    <div style={{ margin: '0 0 18px' }}>
      <button onClick={pagar} disabled={ocupado} style={{ width: '100%', padding: '13px 20px', borderRadius: 10, border: 'none', background: C.navy, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
        {ocupado ? 'Abriendo el pago…' : 'Pagar en línea y seguir trabajando'}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8, fontSize: 12.5, color: C.muted }}>
        <img src="/logo-banchilepagos.png" alt="Banchile Pagos" style={{ height: 28 }} />
        <span>Débito o crédito. Tu estudio se reactiva apenas se confirma el pago.</span>
      </div>
      {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

const Bloqueo: React.FC<Props> = ({ motivo, mensaje, pago, lecturaHasta, onSalir, arriba }) => {
  const titulos: Record<string, string> = {
    vencido: 'Tu período terminó',
    suspendido: 'Tu estudio está suspendido',
    cancelado: 'Cuenta cancelada',
  };
  const mostrarPago = motivo === 'vencido';
  return (
    <div style={{ fontFamily: 'Inter, sans-serif', minHeight: '100vh', background: '#F5F8FC', color: C.navy, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '5vh 20px' }}>
      <LogoLockup height={32} />
      {arriba && <div style={{ maxWidth: 520, width: '100%', marginTop: 20 }}>{arriba}</div>}
      <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 16, padding: '32px 30px', maxWidth: 520, width: '100%', marginTop: 26, textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 25, margin: '0 0 10px' }}>{titulos[motivo] || 'No puedes operar'}</h1>
        <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.6, margin: '0 0 20px' }}>{mensaje}</p>
        {lecturaHasta && (
          <p style={{ fontSize: 14, color: C.navy, background: '#FFF7E6', border: '1px solid #F0DCA8', borderRadius: 10, padding: '10px 14px', margin: '0 0 18px' }}>
            Puedes consultar y descargar tu información hasta el <strong>{new Date(lecturaHasta).toLocaleDateString('es-CL')}</strong>.
          </p>
        )}
        {/* Paga quien tiene con qué identificarse: la sesión abierta, o la corta
            que el login le da solo al admin. */}
        {mostrarPago && pago?.enLinea && (pago.token || getToken()) && <PagarEnLinea token={pago.token} />}
        {mostrarPago && <MediosDePago pago={pago?.enLinea ? { ...pago, link: null } : (pago ?? null)} />}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22, flexWrap: 'wrap' }}>
          <a href="mailto:contacto@tabularis.cl" style={{ padding: '11px 20px', borderRadius: 9, background: C.navy, color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>Escríbenos</a>
          <button onClick={onSalir} style={{ padding: '11px 20px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', color: C.navy, cursor: 'pointer', fontSize: 14 }}>Cerrar sesión</button>
        </div>
      </div>
    </div>
  );
};

export default Bloqueo;
